import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Container, Paper, Breadcrumbs, Link,
  Chip, Avatar, Button, useTheme, alpha, Divider,
  IconButton, Card, CardContent
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  MenuOpen as MenuOpenIcon,
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  Share as ShareIcon,
  Bookmark as BookmarkIcon,
  BookmarkBorder as BookmarkBorderIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';

import Sidebar from '../components/common/Sidebar';
import * as articleApi from '../services/article';
import { Article, Innovation } from '../services/article';

const drawerWidth = 240;

const MainContent = styled(Box)<{ isSidebarOpen: boolean }>(({ theme, isSidebarOpen }) => ({
  flexGrow: 1,
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: isSidebarOpen ? drawerWidth : 0,
  width: '100%'
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -8,
    left: 0,
    width: 60,
    height: 4,
    borderRadius: 2,
    backgroundColor: theme.palette.primary.main,
  }
}));

const MotionBox = motion(Box);

const ArticlePage = () => {
  const { type, id } = useParams();
  const navigate = useNavigate();
  
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [article, setArticle] = useState<Article | Innovation | null>(null);
  const [relatedArticles, setRelatedArticles] = useState<(Article | Innovation)[]>([]);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();

  // Get article data based on type and id
  useEffect(() => {
    const fetchArticle = async () => {
      if (!id || !type) return;

      try {
        setLoading(true);

        if (type === 'news') {
          const articleData = await articleApi.getArticleById(id);
          setArticle(articleData);

          // Get related articles
          const allArticles = await articleApi.getArticles();
          const relatedData = allArticles.filter(a => a.id !== id).slice(0, 3);
          setRelatedArticles(relatedData);
        } else if (type === 'innovation') {
          const innovationData = await articleApi.getInnovationById(id);
          setArticle(innovationData);

          // Get related innovations
          const allInnovations = await articleApi.getInnovations();
          const relatedData = allInnovations.filter(i => i.id !== id).slice(0, 3);
          setRelatedArticles(relatedData);
        }
      } catch (error) {
        console.error('Error fetching article:', error);
        setArticle(null);
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [type, id]);

  const handleBookmark = () => {
    setIsBookmarked(!isBookmarked);
  };

  const goBack = () => {
    navigate(-1);
  };

  // Loading state
  if (loading || !article) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography variant="h5">Loading article...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex' }}>
      
      <MainContent isSidebarOpen={false}>
        
        <Container maxWidth="lg" sx={{ pt: 6, pb: 8 }}>
          {/* Breadcrumbs */}
          <Box sx={{ mb: 3 }}>
            <Breadcrumbs aria-label="breadcrumb">
              <Link 
                color="inherit" 
                onClick={() => navigate('/')}
                sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
              >
                Home
              </Link>
              <Link
                color="inherit"
                onClick={() => navigate('/articles')}
                sx={{ cursor: 'pointer' }}
              >
                {type === 'news' ? 'News' : 'Innovation Spotlight'}
              </Link>
              <Typography color="text.primary">Article</Typography>
            </Breadcrumbs>
          </Box>

          {/* Back Button */}
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={goBack}
            sx={{
              mb: 4,
              borderRadius: 2,
              textTransform: 'none'
            }}
          >
            Back to {type === 'news' ? 'News' : 'Innovation Spotlight'}
          </Button>

          {/* Article Header */}
          <Paper
            elevation={2}
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              mb: 4
            }}
          >
            <Box
              sx={{
                height: 240,
                background: type === 'news'
                  ? `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.8)}, ${alpha(theme.palette.primary.main, 0.9)})`
                  : `linear-gradient(135deg, ${alpha('#FDD835', 0.8)}, ${alpha('#F9A825', 0.9)})`,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                padding: 4,
                position: 'relative',
                color: 'white'
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: '100%',
                  height: '100%',
                  opacity: 0.05,
                  background: `radial-gradient(circle at top right, ${theme.palette.common.white}, transparent 70%)`,
                }}
              />
              
              <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 800 }}>
                <Chip
                  label={type === 'news' ? 'News Article' : 'Innovation Spotlight'}
                  sx={{
                    mb: 2,
                    bgcolor: 'white',
                    color: type === 'news' ? theme.palette.primary.main : '#F9A825',
                    fontWeight: 500
                  }}
                />
                
                <Typography variant="h3" sx={{ fontWeight: 700, mb: 2 }}>
                  {article.title}
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mt: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CalendarIcon sx={{ fontSize: 20, mr: 1, opacity: 0.9 }} />
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      {article.date}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <PersonIcon sx={{ fontSize: 20, mr: 1, opacity: 0.9 }} />
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      {article.author}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <BusinessIcon sx={{ fontSize: 20, mr: 1, opacity: 0.9 }} />
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      {article.industry}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Paper>

          {/* Article Content */}
          <Box sx={{ display: 'flex', gap: 4, flexDirection: { xs: 'column', md: 'row' } }}>
            {/* Main Content */}
            <Box sx={{ flex: '1 1 70%' }}>
              <Paper
                elevation={1}
                sx={{
                  borderRadius: 3,
                  p: 4,
                  mb: 4
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 2, color: 'text.secondary' }}>
                  {article.summary}
                </Typography>
                
                <Divider sx={{ my: 3 }} />
                
                {article.content.map((paragraph, index) => (
                  <Typography 
                    key={index} 
                    variant="body1" 
                    paragraph 
                    sx={{ 
                      mb: 2, 
                      lineHeight: 1.7,
                      ...(index === 0 && {
                        fontSize: '1.1rem',
                        '&::first-letter': {
                          fontSize: '3rem',
                          fontWeight: 'bold',
                          color: type === 'news' ? theme.palette.primary.main : '#F9A825',
                          float: 'left',
                          lineHeight: 0.8,
                          marginRight: '0.2em',
                          marginTop: '0.1em'
                        }
                      })
                    }}
                  >
                    {paragraph}
                  </Typography>
                ))}
                
                {article.keyPoints && (
                  <Box
                    sx={{
                      my: 4,
                      p: 3,
                      bgcolor: alpha(type === 'news' ? theme.palette.primary.main : '#F9A825', 0.05),
                      borderRadius: 2,
                      border: `1px solid ${alpha(type === 'news' ? theme.palette.primary.main : '#F9A825', 0.2)}`
                    }}
                  >
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      Key Points
                    </Typography>

                    {article.keyPoints.map((point, index) => (
                      <Box key={index} sx={{ display: 'flex', mb: 1.5 }}>
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            bgcolor: type === 'news' ? theme.palette.primary.main : '#F9A825',
                            mt: 1,
                            mr: 2,
                            flexShrink: 0
                          }}
                        />
                        <Typography variant="body1">
                          {point}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
                
                {article.conclusion && (
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      mt: 3, 
                      fontStyle: 'italic',
                      lineHeight: 1.7
                    }}
                  >
                    <strong>Conclusion:</strong> {article.conclusion}
                  </Typography>
                )}
                
                {/* Tags */}
                <Box sx={{ mt: 4, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {article.tags.map((tag, index) => (
                    <Chip 
                      key={index} 
                      label={tag} 
                      size="small" 
                      variant="outlined"
                      sx={{ borderRadius: 1 }}
                    />
                  ))}
                </Box>
                
                {/* Action Buttons */}
                <Box 
                  sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    mt: 4,
                    pt: 3,
                    borderTop: `1px solid ${theme.palette.divider}`
                  }}
                >
                  <Button
                    variant="outlined"
                    startIcon={<ShareIcon />}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none'
                    }}
                  >
                    Share Article
                  </Button>
                  
                  <Button
                    variant={isBookmarked ? "contained" : "outlined"}
                    startIcon={isBookmarked ? <BookmarkIcon /> : <BookmarkBorderIcon />}
                    onClick={handleBookmark}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      bgcolor: isBookmarked ? (type === 'news' ? theme.palette.primary.main : '#F9A825') : 'transparent',
                      color: isBookmarked ? 'white' : (type === 'news' ? theme.palette.primary.main : '#F9A825'),
                      borderColor: type === 'news' ? theme.palette.primary.main : '#F9A825',
                      '&:hover': {
                        bgcolor: isBookmarked
                          ? (type === 'news' ? alpha(theme.palette.primary.main, 0.9) : alpha('#F9A825', 0.9))
                          : alpha(type === 'news' ? theme.palette.primary.main : '#F9A825', 0.1)
                      }
                    }}
                  >
                    {isBookmarked ? 'Bookmarked' : 'Bookmark'}
                  </Button>
                </Box>
              </Paper>
            </Box>
            
            {/* Sidebar Content */}
            <Box sx={{ flex: '1 1 30%' }}>
              {/* Related Articles */}
              <Paper
                elevation={1}
                sx={{
                  borderRadius: 3,
                  overflow: 'hidden',
                  mb: 4
                }}
              >
                <Box
                  sx={{
                    p: 2,
                    bgcolor: alpha(type === 'news' ? theme.palette.primary.main : '#F9A825', 0.1),
                    borderBottom: `1px solid ${theme.palette.divider}`
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Related Articles
                  </Typography>
                </Box>

                <Box sx={{ p: 2 }}>
                  {relatedArticles.map((relArticle, index) => (
                    <MotionBox
                      key={index}
                      whileHover={{ x: 5 }}
                      sx={{
                        p: 2,
                        '&:not(:last-child)': {
                          borderBottom: `1px solid ${theme.palette.divider}`
                        },
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: alpha(type === 'news' ? theme.palette.primary.main : '#F9A825', 0.05),
                          cursor: 'pointer'
                        }
                      }}
                      onClick={() => navigate(`/article/${type}/${relArticle.id}`)}
                    >
                      <Typography variant="subtitle1" fontWeight={500}>
                        {relArticle.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {relArticle.date}
                      </Typography>
                    </MotionBox>
                  ))}
                </Box>
              </Paper>
              
              {/* Industry Information */}
              <Paper
                elevation={1}
                sx={{
                  borderRadius: 3,
                  overflow: 'hidden'
                }}
              >
                <Box
                  sx={{
                    p: 2,
                    bgcolor: alpha(theme.palette.info.main, 0.1),
                    borderBottom: `1px solid ${theme.palette.divider}`
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Related Industry
                  </Typography>
                </Box>
                
                <Box sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar
                      sx={{
                        bgcolor: theme.palette.info.main,
                        width: 48,
                        height: 48,
                        mr: 2
                      }}
                    >
                      <BusinessIcon />
                    </Avatar>
                    
                    <Typography variant="h6">
                      {article.industry}
                    </Typography>
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Learn more about investment opportunities and market insights in the {article.industry} sector.
                  </Typography>
                  
                  <Button
                    variant="outlined"
                    color="info"
                    endIcon={<ArrowForwardIcon />}
                    sx={{
                      mt: 1,
                      borderRadius: 2,
                      textTransform: 'none'
                    }}
                    onClick={() => navigate(`/industry/${article.industryId}`)}
                  >
                    Explore Industry
                  </Button>
                </Box>
              </Paper>
            </Box>
          </Box>
          
          {/* More Articles Section */}
          <Box sx={{ mt: 6 }}>
            <SectionHeader>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                More {type === 'news' ? 'News' : 'Innovation'} Articles
              </Typography>
            </SectionHeader>
            
            <Box sx={{ mt: 4 }}>
              <Button
                variant="contained"
                color={type === 'news' ? 'primary' : 'secondary'}
                endIcon={<ArrowForwardIcon />}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  px: 3,
                  py: 1.2,
                  mb: 3
                }}
                onClick={() => navigate('/articles')}
              >
                View All Articles
              </Button>
            </Box>
          </Box>
        </Container>
      </MainContent>
    </Box>
  );
};

export default ArticlePage;